import Icon from "@ant-design/icons/lib/components/Icon";
import { Col, FormInstance, InputNumber, Row, Slider, Typography } from "antd";
import _ from "lodash";
import { useCallback, useMemo } from "react";
import { useQuery, useQueryClient } from "react-query";
import { getDefaultOptions } from "../../../../apis/DefaultOption";
import { ReactComponent as CautionBlank } from "../../../../assets/icons/AlarmFill.svg";
import { theme } from "../../../../theme";
import { CustomForm } from "../../Form/index.styled";

interface UserInfoServiceTireSizeFormProps {
  form: FormInstance<any>;
  readonly?: boolean;
}

export const UserInfoServiceTireSizeForm: React.FC<
  UserInfoServiceTireSizeFormProps
> = ({ form, readonly = false }) => {
  const queryClient = useQueryClient();

  const { data } = useQuery("DefaultOption", getDefaultOptions, {
    initialData: queryClient.getQueryData("DefaultOption"),
    refetchOnMount: "always",
  });

  const isMobile = useMemo(() => {
    return navigator.userAgent.includes("Mobile");
  }, []);

  const minInch = useMemo(
    () => +(data?.find((item) => item.key === "minInch")?.value || 12),
    [data]
  );
  const maxInch = useMemo(
    () => +(data?.find((item) => item.key === "maxInch")?.value || 23),
    [data]
  );

  const displayParsing = useCallback((value?: Array<number>) => {
    if (value?.length) {
      const set = _.uniq(value);
      return `${set.join("-")}인치`;
    }

    return "-";
  }, []);

  return (
    <CustomForm.Item noStyle shouldUpdate>
      {({ getFieldValue, setFieldsValue }) => (
        <Row gutter={12} align="top">
          {!isMobile && (
            <Col>
              <CustomForm.Item
                dependencies={["tireSize"]}
                name={[
                  "repairShopOption",
                  "options",
                  "services",
                  "tire",
                  "minInch",
                ]}
              >
                <InputNumber
                  style={{ width: 60 }}
                  min={minInch}
                  readOnly={readonly}
                  max={
                    getFieldValue([
                      "repairShopOption",
                      "options",
                      "services",
                      "tire",
                      "maxInch",
                    ]) || maxInch
                  }
                  onChange={(e) => {
                    setFieldsValue({
                      tireSize: [e, getFieldValue("tireSize")[1]],
                    });
                  }}
                />
              </CustomForm.Item>
            </Col>
          )}
          <Col>
            <CustomForm.Item name="tireSize">
              <Slider
                disabled={readonly}
                range={{ draggableTrack: true }}
                min={minInch || 10}
                max={maxInch || 30}
                style={{ width: isMobile ? "calc(100vw - 80px)" : 230 }}
                step={1}
                marks={{
                  13: "13",
                  19: "19인치",
                  23: "23",
                }}
                onChange={([min, max]: number[]) => {
                  setFieldsValue({
                    repairShopOption: {
                      options: {
                        services: {
                          tire: {
                            minInch: min,
                            maxInch: max,
                          },
                        },
                      },
                    },
                  });
                }}
              />
            </CustomForm.Item>
          </Col>
          {!isMobile && (
            <Col>
              <CustomForm.Item
                name={[
                  "repairShopOption",
                  "options",
                  "services",
                  "tire",
                  "maxInch",
                ]}
              >
                <InputNumber
                  style={{ width: 60 }}
                  readOnly={readonly}
                  min={
                    getFieldValue([
                      "repairShopOption",
                      "options",
                      "services",
                      "tire",
                      "minInch",
                    ]) ||
                    minInch ||
                    12
                  }
                  max={maxInch}
                  onChange={(e) => {
                    setFieldsValue({
                      tireSize: [getFieldValue("tireSize")[0], e],
                    });
                  }}
                />
              </CustomForm.Item>
            </Col>
          )}
          <Col span={24}>
            <Row align="middle" gutter={4}>
              <Col>
                <Icon component={CautionBlank} />
              </Col>
              <Col>
                <Typography.Text style={{ color: theme.colors.gray2 }}>
                  <Typography.Text strong>
                    {displayParsing(getFieldValue("tireSize"))}
                  </Typography.Text>{" "}
                  가능해요.
                </Typography.Text>
              </Col>
            </Row>
          </Col>
        </Row>
      )}
    </CustomForm.Item>
  );
};
